import { AuthContext, updateLocale } from '@repo-level/core-front';
import { Select } from 'components/form/Select';
import { Locale } from 'languages';
import { addLocaleToPath } from 'modules/locale/paths';
import { setStoredLocale } from 'modules/locale/storage';
import type { FC } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentRoute } from 'react-navi';
import { optionsLanguages } from './helpers';

import { ScLanguageSwitcher, ScLocaleOptionLink } from './styled';

type AvailableLocales = Exclude<
  Locale,
  Locale.SLOVENIAN | Locale.NORWEGIAN | Locale.FRENCH_SWITZERLAND
>;

const LocaleRecord: Record<AvailableLocales, Locale> = {
  [Locale.ENGLISH]: Locale.ENGLISH,
  [Locale.ENGLISH_NEW_ZEALAND]: Locale.ENGLISH_NEW_ZEALAND,
  [Locale.ENGLISH_AUSTRALIA]: Locale.ENGLISH_AUSTRALIA,
  [Locale.ENGLISH_CANADA]: Locale.ENGLISH_CANADA,
  [Locale.FRENCH_CANADA]: Locale.FRENCH_CANADA,
  [Locale.FRENCH]: Locale.FRENCH_SWITZERLAND,
  [Locale.GERMAN]: Locale.GERMAN,
  [Locale.IRISH]: Locale.IRISH,
};

export const LanguageSwitcher: FC = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { isAuthenticated } = useContext(AuthContext);

  const {
    url: { pathname, search, hash },
  } = useCurrentRoute();

  const getLocaleHref = useCallback(
    (lng: string) => `${addLocaleToPath(pathname, lng)}${search}${hash}`,
    [hash, pathname, search]
  );

  const options = useMemo(
    () =>
      optionsLanguages.map((option) => ({
        ...option,
        nameV: option.name,
        name: (
          <ScLocaleOptionLink
            href={getLocaleHref(option.value)}
            hrefLang={option.value}
            onClick={(event) => event.preventDefault()}
          >
            {option.name}
          </ScLocaleOptionLink>
        ),
      })),
    [getLocaleHref]
  );

  // Locale lives in the URL: switching is a full page load.
  const changeLanguage = useCallback(
    async (lng: AvailableLocales) => {
      if (lng === language) return;

      setStoredLocale(lng);

      if (isAuthenticated) {
        try {
          await updateLocale({ locale: LocaleRecord[lng] });
        } catch {
          console.error('Error updating locale');
        }
      }

      window.location.assign(getLocaleHref(lng));
    },
    [getLocaleHref, isAuthenticated, language]
  );

  return (
    <ScLanguageSwitcher>
      <Select
        field="language"
        initialValue={language}
        key={language}
        lightBg
        lightColoredDropMode
        maxHeight={268}
        onSelect={(lang: any) => {
          void changeLanguage(lang);
        }}
        options={options}
        placeholder={t('glossary:region')}
        up
        withBorderArrow
      />
    </ScLanguageSwitcher>
  );
};
